import React from 'react';
import { Grant, statusBadge } from '../../lib/a2aApi';
import { Icon } from '../ui';
import './a2a.css';
import { useTranslation } from 'react-i18next';

interface Props { grants: Grant[]; canWrite: boolean; onRevoke: (g: Grant) => void; now?: number; nameOf?: (id: string) => string }

const remaining = (exp: number, now: number) => {
  const s = exp - Math.floor(now / 1000);
  if (s <= 0) return 'expired';
  return s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m` : s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
};

const GrantTree: React.FC<Props> = ({ grants, canWrite, onRevoke, now = Date.now(), nameOf }) => {
  const { t } = useTranslation();
  const label = (id: string) => (nameOf ? nameOf(id) : id);
  const byParent = new Map<string | null, Grant[]>();
  grants.forEach((g) => byParent.set(g.parent_grant_id, [...(byParent.get(g.parent_grant_id) || []), g]));
  const render = (parent: string | null): React.ReactNode => {
    const kids = byParent.get(parent) || [];
    if (!kids.length) return null;
    return (
      <ul>
        {kids.map((g) => (
          <li key={g.grant_id}>
            <div className={`a2a-grant ${g.status === 'revoked' ? 'revoked' : ''}`}>
              <span className="a2a-muted">{g.parent_grant_id ? `${label(g.issuer)} →` : `${g.principal_user_id} →`}</span>
              <span className="a2a-agent">{label(g.audience_agent_id)}</span>
              <span className={`badge ${statusBadge(g.status)}`}>{g.status}</span>
              {g.status === 'active' && <span className="a2a-chip">{t('components.a2a.grantTree.ttl', { v0: remaining(g.expires_at, now) })}</span>}
              {g.cap?.usd != null && <span className="a2a-chip">{t('components.a2a.grantTree.capUsd', { usd: g.cap.usd.toLocaleString() })}{g.spentUsd ? ` · ${t('components.a2a.grantTree.spentUsd', { usd: g.spentUsd.toLocaleString() })}` : ''}</span>}
              <span className="a2a-chip" title={g.scope.join(', ')}>{t('components.a2a.grantTree.tools', { v0: g.scope.length })}</span>
              <span className="a2a-mono a2a-muted">{g.grant_id}</span>
              {g.status === 'revoked' && <span className="a2a-muted">{t('components.a2a.grantTree.revokedBy', { name: g.revoked_by })}{g.revoke_reason ? ` — ${g.revoke_reason}` : ''}</span>}
              {canWrite && g.status === 'active' && (
                <button className="ent-btn sm" style={{ marginLeft: 'auto' }} onClick={() => onRevoke(g)}><Icon name="deny" /> {t('components.a2a.grantTree.revokeSubtree')}</button>
              )}
            </div>
            {render(g.grant_id)}
          </li>
        ))}
      </ul>
    );
  };
  if (!grants.length) return <div className="ent-empty">{t('components.a2a.grantTree.noDelegationGrantsYet')}</div>;
  return <div className="a2a-tree">{render(null)}</div>;
};

export default GrantTree;
